import { useState } from "react";
import { Gift, MapPin, Check, Clock, Phone, Navigation, Sparkles } from "lucide-react";
import { CenterAddress, isAddressNew } from "@/data/cities";

interface DonorNumberCardProps {
  provider: string;
  description: string;
  donorNumber: string;
  bonusInfo: string;
  addresses?: CenterAddress[];
  locations?: string[];
  locationSummary?: string;
  accentColor?: "primary" | "bonus";
  wide?: boolean;
}

const DonorNumberCard = ({
  provider,
  description,
  donorNumber,
  bonusInfo,
  addresses,
  locations,
  locationSummary,
  accentColor = "primary",
  wide = false,
}: DonorNumberCardProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(donorNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const gradientClass =
    accentColor === "bonus" ? "bonus-gradient" : "success-gradient";
  const glowClass = accentColor === "bonus" ? "glow-bonus" : "glow-accent";

  return (
    <div
      className={`card-modern overflow-hidden border border-border ${glowClass}`}
    >
      {/* Header */}
      <div className={`p-6 ${gradientClass}`}>
        <div className="flex items-center gap-4">
          <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold font-display text-white">
            {provider}
          </h3>
        </div>
      </div>

      {/* Content */}
      <div className={wide ? "p-6 md:p-8 md:grid md:grid-cols-2 md:gap-8" : "p-6"}>
        <div>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          {description}
        </p>

        {/* Donor Number - clickable to copy */}
        <div
          onClick={handleCopy}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleCopy();
            }
          }}
          className="donor-number-card rounded-xl p-5 md:p-6 mb-6 cursor-pointer select-none transition-all active:scale-[0.99] hover:border-primary/40 relative group"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              Spendernummer für Anmeldebonus
            </p>
            {copied && (
              <span className="text-xs text-bonus font-semibold flex items-center gap-1 animate-fade-in">
                <Check className="w-3.5 h-3.5" />
                Kopiert!
              </span>
            )}
          </div>
          <span className="donor-number-text text-2xl md:text-3xl font-extrabold animate-glow block">
            {donorNumber}
          </span>
          <p className="text-xs text-muted-foreground mt-3">
            Bei der Erstanmeldung vor Ort angeben
          </p>
        </div>

        </div>

        <div>
        {/* Addresses (on CityPage) */}
        {addresses && addresses.length > 0 && (
          <div className="glass-card rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">
                {addresses.length === 1
                  ? "Adresse & Kontaktdaten"
                  : `${addresses.length} Standorte`}
              </span>
            </div>
            <div
              className={`grid gap-4 ${addresses.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"}`}
            >
              {addresses.map((addr, index) => {
                const isNew = isAddressNew(addr);
                return (
                  <div
                    key={index}
                    className={`flex flex-col gap-1.5 p-3.5 bg-muted/30 rounded-lg border transition-all ${
                      isNew
                        ? "border-emerald-500/40 bg-emerald-500/5 shadow-[0_0_15px_-3px_rgba(16,185,129,0.15)]"
                        : "border-border/50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      {addr.label && (
                        <span className="text-xs font-bold text-primary uppercase tracking-wider">
                          {addr.label}
                        </span>
                      )}
                      {isNew && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-500/20 border border-emerald-500/30 px-2 py-0.5 rounded-full shadow-sm">
                          <Sparkles className="w-2.5 h-2.5" />
                          Neu eröffnet
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-foreground font-medium">
                      {addr.street}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {addr.zip} {addr.city}
                    </div>

                    {addr.openingHours && (
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 pt-1.5 border-t border-border/40">
                        <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span>{addr.openingHours}</span>
                      </div>
                    )}

                    {addr.phone && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-primary shrink-0" />
                        <a
                          href={`tel:${addr.phone.replace(/\s+/g, "")}`}
                          className="hover:text-foreground transition-colors"
                        >
                          {addr.phone}
                        </a>
                      </div>
                    )}

                    {addr.publicTransport && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Navigation className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span>{addr.publicTransport}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Locations list (on Homepage BonusSection) */}
        {locations && locations.length > 0 && (
          <div className="glass-card rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-2.5">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">
                {locationSummary ?? `${locations.length} Standorte in Deutschland`}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {locations.map((loc, index) => {
                const isNew = loc.includes("Neu");
                return (
                  <span
                    key={index}
                    className={`text-xs px-2.5 py-1 rounded-md border transition-all ${
                      isNew
                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40 font-semibold shadow-sm"
                        : "bg-muted/40 text-muted-foreground border-border/50"
                    }`}
                  >
                    {loc}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Bonus info */}
        <div className="glass-card rounded-xl p-4">
          <p className="text-sm text-foreground">
            <span className="text-bonus font-semibold">💡 Bonus:</span>{" "}
            <span className="text-muted-foreground">{bonusInfo}</span>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default DonorNumberCard;
